import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import type { InjectionKey } from 'vue'
import { computed, inject, provide, reactive, ref, watch } from 'vue'

import { getBrandBranding, upsertPlatformSettings } from '@/features/admin/platform-setup/api'
import type {
  PlatformSettingNameType,
  PlatformSettingsValuesType,
  SaveSectionType,
} from '@/features/admin/platform-setup/constants'
import { PLATFORM_SETUP_STATUS, STEP_FIELDS } from '@/features/admin/platform-setup/constants'
import { computeChecklist, isSamlConfigured, toSettingsMap } from '@/features/admin/platform-setup/utils/checklist'
import { markNavAutoOpen, markSetupDismissed } from '@/features/admin/platform-setup/utils/dismissal'
import { settingsApi } from '@/features/admin/settings/api'
import { WHITE_LABEL_QUERY_KEY } from '@/features/ui/api/global-data'

// Même clé que la page des paramètres : les deux écrans partagent le cache.
export const PLATFORM_SETTINGS_QUERY_KEY = 'settings'

export type SsoModeType = 'itTeam' | 'self'

const EMPTY_VALUES: PlatformSettingsValuesType = {
  TITLE: '',
  COMPANY_NAME: '',
  LOGO: '',
  LOGO_SMALL: '',
  FAVICON: '',
  COLOR_PRIMARY: '',
  COLOR_SECONDARY: '',
  CUSTOM_STYLE: '',
  EMAIL_LOGIN_ENABLED: 'true',
  SAML_LOGIN_ENABLED: 'false',
  SAML_IDP_SSO_URL: '',
  SAML_CERTIFICATE: '',
  LOGO_SSO: '',
  SUPPORT_EMAIL: '',
  SUPPORT_PHONE: '',
  EXTERNAL_RESOURCES_URL: '',
  LEGAL_RT_ORGANISME_NAME: '',
  LEGAL_RT_CONTACT_ADDRESS: '',
  LEGAL_RT_DPO_EMAIL: '',
  PLATFORM_SETUP_STATUS: '',
  ENABLE_TRANSACTIONAL_EMAILS: 'true',
}

export class LoginStepError extends Error {}
export class EmailsCutNotConfirmedError extends Error {}

function createPlatformSetup() {
  const queryClient = useQueryClient()

  const settingsQuery = useQuery({
    queryKey: [PLATFORM_SETTINGS_QUERY_KEY],
    queryFn: settingsApi.getAllSettings,
  })

  const savedRef = computed(() => toSettingsMap(settingsQuery.data.value))
  const checklistRef = computed(() => computeChecklist(savedRef.value))

  // Brouillon édité par les étapes, initialisé une fois depuis la base.
  const form = reactive<PlatformSettingsValuesType>({ ...EMPTY_VALUES })
  const emailLoginRef = ref(true)
  const ssoWantedRef = ref(false)
  const ssoModeRef = ref<SsoModeType>('itTeam')
  const isInitializedRef = ref(false)
  // Couper les e-mails automatiques demande une confirmation explicite (cf. emails-fields.vue).
  const emailsCutConfirmedRef = ref(false)

  /** Remet le brouillon sur les valeurs enregistrées (abandon d'une modification). */
  function resetForm() {
    const saved = savedRef.value
    for (const name of Object.keys(EMPTY_VALUES) as PlatformSettingNameType[]) {
      form[name] = saved[name] ?? EMPTY_VALUES[name]
    }
    emailLoginRef.value = form.EMAIL_LOGIN_ENABLED !== 'false'
    emailsCutConfirmedRef.value = false
    ssoWantedRef.value = form.SAML_LOGIN_ENABLED === 'true' || !!form.SAML_IDP_SSO_URL
    ssoModeRef.value = isSamlConfigured(saved) || form.SAML_IDP_SSO_URL ? 'self' : 'itTeam'
  }

  watch(
    () => settingsQuery.data.value,
    (rows) => {
      if (!rows || isInitializedRef.value) return
      resetForm()
      isInitializedRef.value = true
    },
    { immediate: true },
  )

  const saveMutation = useMutation({
    mutationFn: upsertPlatformSettings,
    onSuccess: (rows) => {
      queryClient.setQueryData([PLATFORM_SETTINGS_QUERY_KEY], rows)
      queryClient.invalidateQueries({ queryKey: [WHITE_LABEL_QUERY_KEY] })
    },
  })

  /** Ce que l'étape connexion enregistre. Lève une erreur si plus personne ne peut se connecter. */
  function loginPayload(): Partial<PlatformSettingsValuesType> {
    const samlReady =
      ssoWantedRef.value &&
      ssoModeRef.value === 'self' &&
      form.SAML_IDP_SSO_URL.trim() !== '' &&
      form.SAML_CERTIFICATE.trim() !== ''
    if (!emailLoginRef.value && !samlReady) {
      throw new LoginStepError('admin.setup.login.errors.noMethod')
    }
    const payload: Partial<PlatformSettingsValuesType> = {
      EMAIL_LOGIN_ENABLED: String(emailLoginRef.value),
      SAML_LOGIN_ENABLED: String(samlReady),
      LOGO_SSO: form.LOGO_SSO.trim(),
    }
    if (ssoWantedRef.value && ssoModeRef.value === 'self') {
      payload.SAML_IDP_SSO_URL = form.SAML_IDP_SSO_URL.trim()
      payload.SAML_CERTIFICATE = form.SAML_CERTIFICATE.trim()
    }
    return payload
  }

  function stepPayload(step: SaveSectionType): Partial<PlatformSettingsValuesType> {
    if (step === 'recap' || step === 'team') return {}
    if (step === 'login') return loginPayload()
    const payload: Partial<PlatformSettingsValuesType> = {}
    for (const name of STEP_FIELDS[step]) {
      payload[name] = form[name].trim()
    }
    // La plateforme n'affiche plus que le petit logo, qui sert aussi de favicon : un seul
    // logo est saisi, recopié dans les trois réglages pour que tous les écrans concordent.
    if (step === 'branding') {
      payload.LOGO = payload.LOGO_SMALL
      payload.FAVICON = payload.LOGO_SMALL
    }
    return payload
  }

  async function saveStep(step: SaveSectionType) {
    const isCuttingEmails =
      step === 'emails' &&
      form.ENABLE_TRANSACTIONAL_EMAILS !== 'true' &&
      savedRef.value.ENABLE_TRANSACTIONAL_EMAILS === 'true'
    if (isCuttingEmails && !emailsCutConfirmedRef.value) {
      throw new EmailsCutNotConfirmedError('admin.platform.sections.emails.errors.confirm')
    }
    const payload = stepPayload(step)
    if (Object.keys(payload).length === 0) return
    await saveMutation.mutateAsync(payload)
  }

  async function complete() {
    await saveMutation.mutateAsync({ PLATFORM_SETUP_STATUS: PLATFORM_SETUP_STATUS.COMPLETED })
    // Le tableau de bord lirait sinon l'ancien statut en cache et renverrait ici.
    markSetupDismissed()
    markNavAutoOpen()
    await queryClient.invalidateQueries({ queryKey: [WHITE_LABEL_QUERY_KEY] })
  }

  /** Préremplit le brouillon depuis le site web ; rend les champs trouvés. */
  /** Préremplit le logo et les couleurs depuis le site web ; les noms ne sont jamais touchés. */
  async function autofillFromWebsite(url: string): Promise<{ logo: boolean; colors: boolean }> {
    const found = await getBrandBranding(url)
    if (found.logo) form.LOGO_SMALL = found.logo
    if (found.colorPrimary) form.COLOR_PRIMARY = found.colorPrimary
    if (found.colorSecondary) form.COLOR_SECONDARY = found.colorSecondary
    return { logo: !!found.logo, colors: !!found.colorPrimary }
  }

  return {
    form,
    emailLoginRef,
    ssoWantedRef,
    ssoModeRef,
    emailsCutConfirmedRef,
    savedRef,
    checklistRef,
    isLoadingRef: settingsQuery.isPending,
    isErrorRef: settingsQuery.isError,
    // 403 : le compte n'est pas super admin, « Ma plateforme » lui est fermée.
    isForbiddenRef: computed(() => (settingsQuery.error.value as { status?: number } | null)?.status === 403),
    isSavingRef: saveMutation.isPending,
    saveStep,
    resetForm,
    complete,
    autofillFromWebsite,
  }
}

export type PlatformSetupType = ReturnType<typeof createPlatformSetup>

const PLATFORM_SETUP_KEY: InjectionKey<PlatformSetupType> = Symbol('platform-setup')

export function providePlatformSetup() {
  const setup = createPlatformSetup()
  provide(PLATFORM_SETUP_KEY, setup)
  return setup
}

export function usePlatformSetup() {
  const setup = inject(PLATFORM_SETUP_KEY)
  if (!setup) throw new Error('usePlatformSetup must be used under providePlatformSetup')
  return setup
}
